import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useIsFetching } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { format, differenceInMinutes, addDays, startOfWeek, addWeeks } from "date-fns";
import { toast } from "sonner";
// UPPDRAG-31: addMinutes + checkSlotAvailability + calculateBookingPrice
// no longer used here — drag-drop now opens MoveBookingDialog (which
// owns the price calc) and validateDropZone in scheduleGridDropHelpers
// owns the conflict check.
import AdminBookingSheet from "@/components/admin/AdminBookingSheet";
import MoveBookingDialog from "@/components/admin/MoveBookingDialog";
import SubscriptionMoveDialog from "@/components/admin/SubscriptionMoveDialog";
import { formatPrice } from "@/utils/formatPrice";
// Lager 3a — same-price guard for click-to-move drop targets (P3-gap
// mitigation; MoveBookingDialog in main uses flat pricing).
// Path B+ — canonical pris-resolver för admin-direct booking-INSERT
// (M1-fix). Per-seat-formel för private bookings inkluderar member-cat.
import { calculateBookingPrice, resolveBookingTargetPrice, bookingKindForPricing } from "@/lib/pricing";
import { getPlayerCategory } from "@/lib/playerPricing";
import { cn } from "@/lib/utils";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";

import { useScheduleGridData } from "@/hooks/useScheduleGridData";
import { ScheduleGridToolbar } from "@/components/admin/schedule/ScheduleGridToolbar";
import { ScheduleGridHeader } from "@/components/admin/schedule/ScheduleGridHeader";
import { ScheduleGridRow } from "@/components/admin/schedule/ScheduleGridRow";
import { RecurringBlockDetailDialog } from "@/components/admin/schedule/RecurringBlockDetailDialog";
import { MatchDetailModal } from "@/components/admin/MatchDetailModal";
import { ActivityDetailModal } from "@/components/admin/ActivityDetailModal";
import { SubscriptionDetailDialog } from "@/components/admin/schedule/SubscriptionDetailDialog";
import { BookingDetailPanel } from "@/components/admin/schedule/BookingDetailPanel";
import { CATEGORY_STYLES } from "@/components/admin/schedule/types";
import type { ScheduleBooking, SelectionRect } from "@/components/admin/schedule/types";
import { validateCourtAvailability } from "@/lib/validateCourtAvailability";
// UPPDRAG-31: drag-drop now opens MoveBookingDialog instead of calling
// move_booking directly — kronorToMinor is no longer used by this file.
// validateDropZone + resolveInitialTargetFromDrop centralise the drop
// math so both the visual feedback (during drag) and the hard rejection
// (on drop) use the same rules.
import {
  validateDropZone,
  resolveInitialTargetFromDrop,
  resolveAndRouteMove,
  type MoveResolution,
} from "@/lib/scheduleGridDropHelpers";
import BulkCancelConfirmDialog from "@/components/admin/BulkCancelConfirmDialog";
import { Button } from "@/components/ui/button";
import { XCircle } from "lucide-react";

export default function AdminScheduleGrid() {
  const { t } = useTranslation("admin");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  // Lager 1 — read-only side panel for non-subscription non-linked bookings.
  // Routed to from routeBookingClick fallback branch (plain private + legacy
  // is_open_match-without-canonical-link). Replaces the previous
  // BookingDetailModal entry from grid clicks (UPPDRAG-31 keeps drag-drop as
  // the mutation flow via MoveBookingDialog).
  const [selectedBookingPanel, setSelectedBookingPanel] = useState<ScheduleBooking | null>(null);
  // Lager 3a — click-to-move from BookingDetailPanel. When the panel's
  // "Flytta tid"-button is pressed we close the panel and set the source
  // booking here; the grid then enters a banner-state that intercepts the
  // next empty-slot click as a drop target. Drag-drop flow (UPPDRAG-31)
  // is unchanged and orthogonal. P3-gap mitigation: drop is rejected with
  // a toast if the target slot's standard price differs from the source's
  // (MoveBookingDialog uses flat pricing in main; Lager 3b will replace it).
  const [clickToMoveBooking, setClickToMoveBooking] = useState<ScheduleBooking | null>(null);
  const [moveBooking, setMoveBooking] = useState<ScheduleBooking | null>(null);
  // UPPDRAG-31: when MoveBookingDialog opens via drag-drop, pre-fill its
  // "TILL" fields with the drop target so the admin sees what they're
  // confirming. Null when opened from the menu (existing flow).
  const [moveDropTarget, setMoveDropTarget] = useState<{ courtId: string; date: string; time: string } | null>(null);
  const [subscriptionMoveBooking, setSubscriptionMoveBooking] = useState<{ booking: ScheduleBooking; type: "recurring" | "activity" | "training" } | null>(null);

  // Multi-court rectangular selection
  const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [highlightedCourtIds, setHighlightedCourtIds] = useState<string[]>([]);
  const [selectedRecurringBlock, setSelectedRecurringBlock] = useState<any | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<any | null>(null);
  // Tidsbokning Phase A — Seriespel click → MatchDetailModal (just match_id;
  // the modal fetches the full match row itself).
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  // activity-click-fix — open_match-linked booking click -> ActivityDetailModal
  // (open_matches.id; modal uses useActivityDetail for full data fetch).
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null);
  // shadow-rendering-drag-fix Gap 1B — drop target stash for activity drag.
  // Mirrors matchDropInitialTarget. When set, ActivityDetailModal auto-opens
  // ActivityRescheduleDialog with these fields pre-filled.
  const [activityDropInitialTarget, setActivityDropInitialTarget] = useState<{ courtId: string; date: string; time: string } | null>(null);

  // overlap-defense Fix C — block subsequent drag-starts while any
  // schedule query is refetching (post-RPC). Per
  // audit-reports/investigations/overlap-rejection-failure-critical.md §6.3:
  // the operator's 5 rapid drag-moves in 78s caused React Query refetch
  // lag and stale-state validation in handleBookingDrop. Guard reads
  // useIsFetching for the schedule query keys we care about and treats
  // any in-flight refetch as a "reschedule busy" signal.
  //
  // useIsFetching returns a count >= 0 of running fetches matching the
  // predicate; > 0 means refetch in flight.
  const scheduleFetchCount = useIsFetching({
    predicate: (q) => {
      const key = q.queryKey[0];
      return key === "schedule-bookings"
        || key === "schedule-league-matches"
        || key === "schedule-recurring"
        || key === "schedule-recurring-blocks"
        || key === "schedule-sub-releases";
    },
  });
  const isRescheduleInFlight = scheduleFetchCount > 0;

  // PR-AK — Multi-select bulk-cancel mode. When ON, clicking a confirmed
  // booking toggles selection instead of opening BookingDetailModal.
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedBookingIds, setSelectedBookingIds] = useState<Set<string>>(new Set());
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);

  const toggleBookingSelection = useCallback((id: string) => {
    setSelectedBookingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Container ref for dynamic height calculation
  const containerRef = useRef<HTMLDivElement>(null);
  const [gridHeight, setGridHeight] = useState<number | null>(null);

  // Now indicator – updates every minute
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const dragRef = useRef<{
    active: boolean;
    anchorCourtIdx: number;
    anchorSlotIdx: number;
    mode: "select" | "deselect";
  } | null>(null);

  // Booking drag-to-move state.
  // UPPDRAG-31: extended with `dropValid: boolean | null` so the grid
  // cells can paint the proposed drop range green (valid) or red
  // (invalid — out of bounds or slot occupied) during the drag, before
  // the user releases. null = haven't computed yet (drag just started).
  const [bookingDrag, setBookingDrag] = useState<{
    booking: ScheduleBooking;
    originCourtIdx: number;
    originSlotIdx: number;
    targetCourtIdx: number;
    targetSlotIdx: number;
    slotCount: number;
    dropValid: boolean | null;
  } | null>(null);
  const bookingDragStartRef = useRef<{ x: number; y: number; booking: ScheduleBooking; courtIdx: number; slotIdx: number; slotCount: number } | null>(null);
  const [dragPointer, setDragPointer] = useState<{ x: number; y: number } | null>(null);
  const MIN_DRAG_DISTANCE = 8;

  // Tidsbokning Phase D — Seriespel drag state. Mirrors bookingDrag.
  // matchDragStartRef stashes pointer + match metadata until MIN_DRAG_DISTANCE
  // is crossed; only then does matchDrag state activate (so plain clicks
  // pass through to onMatchClick).
  const [matchDrag, setMatchDrag] = useState<{
    matchId: string;
    originCourtIdx: number;
    originSlotIdx: number;
    targetCourtIdx: number;
    targetSlotIdx: number;
    slotCount: number;
    durationMinutes: number;
    dropValid: boolean | null;
  } | null>(null);
  const matchDragStartRef = useRef<{ x: number; y: number; matchId: string; courtIdx: number; slotIdx: number; slotCount: number; durationMinutes: number } | null>(null);
  // initialRescheduleTarget piped into MatchDetailModal when drop opens the
  // reschedule dialog with a pre-filled TILL.
  const [matchDropInitialTarget, setMatchDropInitialTarget] = useState<{ courtId: string; date: string; time: string } | null>(null);

  useEffect(() => {
    const endDrag = () => {
      if (dragRef.current?.active) {
        dragRef.current = null;
        setIsDragging(false);
      }
    };
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchend", endDrag);
    return () => {
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchend", endDrag);
    };
  }, []);

  const {
    user,
    organization,
    hall,
    hallId,
    hallHours,
    courts,
    bookings,
    pricingRules,
    catByUser,
    timeSlots,
    kpis,
    getBookingCategory,
    getBookingAt,
    getTrainingAt,
    getTrainingSlotInfo,
    getPlayerName,
    // activity-click-fix
    getLinkedOpenMatch,
    getActivityLabel,
    // schedule-grid-gap-1-fix
    getLinkedLeagueMatch,
    getLeagueMatchLabel,
    computeSelectionInfo,
    invalidateBookings,
    recurringBookings,
    getRecurringBlockAt,
    getRecurringBookingAt,
    getSubscriptionReleaseAt,
    rbCustomerMap,
    // PR-D Layer 1 (TASK-75) — placed league_match overlay
    getLeagueMatchAt,
    getLeagueMatchSlotInfo,
  } = useScheduleGridData(selectedDate);

  // FIX #1: Calculate dynamic row height
  useEffect(() => {
    const calcHeight = () => {
      if (!containerRef.current || courts.length === 0) return;
      const vh = window.innerHeight;
      const headerOffset = containerRef.current.getBoundingClientRect().top;
      const isMobile = window.innerWidth < 640;
      const reservedBelow = isMobile ? 180 : 140;
      const availableHeight = vh - headerOffset - reservedBelow;
      const timeHeaderHeight = 28;
      const minRowHeight = isMobile ? 48 : 60;
      const perRow = Math.max(minRowHeight, Math.floor((availableHeight - timeHeaderHeight) / courts.length));
      setGridHeight(perRow);
    };
    calcHeight();
    window.addEventListener("resize", calcHeight);
    return () => window.removeEventListener("resize", calcHeight);
  }, [courts.length]);

  const rowHeight = gridHeight ?? 80;

  const nowPosition = useMemo(() => {
    if (timeSlots.length === 0) return null;
    // Compute "now" as a % of the rendered grid's time extent, not hallHours —
    // timeSlots can widen beyond hallHours to include overflow bookings.
    const first = timeSlots[0];
    const last = timeSlots[timeSlots.length - 1];
    const startMin = first.getHours() * 60 + first.getMinutes();
    const endMin = last.getHours() * 60 + last.getMinutes() + 30; // slot width
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const isToday = now.toDateString() === selectedDate.toDateString();
    if (!isToday || nowMin < startMin || nowMin > endMin) return null;
    return ((nowMin - startMin) / (endMin - startMin)) * 100;
  }, [now, timeSlots, selectedDate]);

  const handleDragStart = useCallback((courtIdx: number, slotIdx: number) => {
    const courtId = courts[courtIdx]?.id;
    const slot = timeSlots[slotIdx];
    if (!courtId || !slot) return;
    const existing = getBookingAt(courtId, slot);
    // Allow drag-start on blocked slots (for unblocking), but not on other bookings
    if (existing && existing.status !== "blocked") return;
    if (getTrainingAt(courtId, slot)) return;
    // PR-D Layer 1 (TASK-75): league_match slots are reserved — don't start
    // a selection drag here. The DB trigger (Layer 4) would reject any insert
    // anyway, but suppressing the drag is cleaner UX.
    // Tidsbokning Phase A: silent bail (was: toast.info). The Seriespel block
    // overlay has its own onClick → MatchDetailModal, so no UX clue needed here.
    if (getLeagueMatchAt(courtId, slot)) {
      return;
    }
    dragRef.current = { active: true, anchorCourtIdx: courtIdx, anchorSlotIdx: slotIdx, mode: "select" };
    setIsDragging(true);
    setSelectionRect({ startCourtIdx: courtIdx, endCourtIdx: courtIdx, startSlotIdx: slotIdx, endSlotIdx: slotIdx });
  }, [courts, timeSlots, getBookingAt, getTrainingAt, getLeagueMatchAt, t]);

  const handleDragEnter = useCallback((courtIdx: number, slotIdx: number) => {
    const drag = dragRef.current;
    if (!drag?.active) return;
    setSelectionRect({
      startCourtIdx: drag.anchorCourtIdx,
      endCourtIdx: courtIdx,
      startSlotIdx: drag.anchorSlotIdx,
      endSlotIdx: slotIdx,
    });
  }, []);

  // activity-click-fix + schedule-grid-gap-1-fix — shared booking-click
  // router. When the booking is the blocker for a higher-level entity
  // (linked via reverse FK), open the entity's detail surface; otherwise
  // fall back to BookingDetailModal. Used by:
  //   - handleBookingClickGrid (schedule cell, with multi-select guard)
  //   - handleSlotClick (slot click that hit an existing booking)
  //   - ScheduleGridToolbar (search-result click)
  // Declared ABOVE handleSlotClick to avoid TDZ in the useCallback dep
  // array (smoke render test hit this on rerender — Lesson 7).
  //
  // Routing precedence (most specific first):
  //   1. linked_league_match -> MatchDetailModal (PR #249 backfill bookings)
  //   2. linked_open_match   -> ActivityDetailModal (PR #251)
  //   3. plain booking       -> BookingDetailPanel (Lager 1 — read-only panel)
  //      Previously: BookingDetailModal. Drag-to-move flow on the grid still
  //      opens MoveBookingDialog (UPPDRAG-31); admins use that for mutations.
  const routeBookingClick = useCallback((booking: ScheduleBooking) => {
    const llm = getLinkedLeagueMatch(booking);
    if (llm?.id) {
      // schedule-grid-gap-1-fix — reuse selectedMatchId state from PR #242
      // (Seriespel block click); MatchDetailModal is the same surface.
      setMatchDropInitialTarget(null);
      setSelectedMatchId(llm.id);
      return;
    }
    const lom = getLinkedOpenMatch(booking);
    if (lom?.id) {
      setSelectedActivityId(lom.id);
      return;
    }
    setSelectedBookingPanel(booking);
  }, [getLinkedLeagueMatch, getLinkedOpenMatch]);

  // Lager 3a — Esc cancels click-to-move mode cleanly. Listener bound only
  // while a source booking is set so other Esc consumers are unaffected.
  useEffect(() => {
    if (!clickToMoveBooking) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setClickToMoveBooking(null);
        toast.info(t("scheduleGrid.moveCancelled", { defaultValue: "Flytt avbruten." }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clickToMoveBooking, t]);

  // Lager 3a — callback for BookingDetailPanel's "Flytta tid"-button.
  // Activates click-to-move mode for the selected booking and closes the panel.
  const activateClickToMove = useCallback((booking: ScheduleBooking) => {
    setSelectedBookingPanel(null);
    setClickToMoveBooking(booking);
  }, []);

  // Lager 3a-fix — canonical switch-helper for the validate+route pipeline.
  // Both drag-drop (handleBookingDrop) and click-to-move (handleSlotClick
  // intercept) call resolveAndRouteMove + apply the result here. Centralised
  // so the two paths can never disagree on toast wording or routing target
  // (the diagnosis-confirmed §3/§6 bug class).
  const applyMoveResolution = useCallback(
    (resolution: MoveResolution, booking: ScheduleBooking) => {
      switch (resolution.kind) {
        case "blocked":
          toast.error(
            resolution.reason === "out_of_bounds"
              ? t("scheduleGrid.dropOutOfBounds", {
                  defaultValue: "Bokningen får inte plats — rutan slutar för tidigt på dagen.",
                })
              : t("scheduleGrid.slotOccupied"),
          );
          return;
        case "league":
          setMatchDropInitialTarget(resolution.dropTarget);
          setSelectedMatchId(resolution.matchId);
          return;
        case "activity":
          setActivityDropInitialTarget(resolution.dropTarget);
          setSelectedActivityId(resolution.activityId);
          return;
        case "group":
          setMoveDropTarget(resolution.dropTarget);
          setMoveBooking(booking);
          return;
        case "recurring":
          setMoveDropTarget(resolution.dropTarget);
          setSubscriptionMoveBooking({ booking, type: "recurring" });
          return;
        case "legacy_activity":
          setMoveDropTarget(resolution.dropTarget);
          setSubscriptionMoveBooking({ booking, type: "activity" });
          return;
        case "regular":
          setMoveDropTarget(resolution.dropTarget);
          setMoveBooking(booking);
          return;
      }
    },
    [t],
  );

  const handleSlotClick = useCallback((courtIdx: number, slotIdx: number) => {
    const courtId = courts[courtIdx]?.id;
    const slot = timeSlots[slotIdx];
    if (!courtId || !slot) return;
    const existingBooking = getBookingAt(courtId, slot);

    // Lager 3a — intercept while click-to-move is active.
    if (clickToMoveBooking) {
      // Clicking another booking → cancel move-mode and open that booking's
      // panel (treat as "changed mind"). The original move-mode does not
      // persist across detail-panel changes.
      if (existingBooking) {
        setClickToMoveBooking(null);
        routeBookingClick(existingBooking as ScheduleBooking);
        return;
      }
      // Clicking a league-match overlay → cancel move-mode silently (slot
      // is structurally unavailable for click-to-move drop).
      if (getLeagueMatchAt(courtId, slot)) {
        setClickToMoveBooking(null);
        toast.info(t("scheduleGrid.moveCancelled", { defaultValue: "Flytt avbruten." }));
        return;
      }
      // Empty slot — this is the drop target. P3-gap mitigation: compare
      // STANDARD-rate price at source vs target. If they differ, refuse
      // and keep mode active so the operator can pick another slot.
      // Special-date holidays are not considered here (MoveBookingDialog
      // surfaces them in its own confirmation); we gate on the most-common
      // pricing-rule axis only.
      const src = clickToMoveBooking;
      const srcStart = new Date(src.start_time);
      const srcPrice = calculateBookingPrice(
        pricingRules, hallId, srcStart, src.duration_minutes, "standard", [],
      ).total;
      const tgtPrice = calculateBookingPrice(
        pricingRules, hallId, slot, src.duration_minutes, "standard", [],
      ).total;
      if (srcPrice !== tgtPrice) {
        toast.error(
          t("scheduleGrid.movePriceMismatch", {
            defaultValue: "Flytt blockerad: målslotens pris ({{tgt}} kr) skiljer sig från bokningens nuvarande pris ({{src}} kr). Välj en slot med samma pris.",
            src: srcPrice,
            tgt: tgtPrice,
          }),
        );
        return; // Stay in move-mode; let operator try another slot
      }
      // Lager 3a-fix — route through the same validate+pipeline as drag-drop.
      // slotCount derivation: useScheduleGridData generates timeSlots with a
      // HARDCODED 30-min step (src/hooks/useScheduleGridData.ts:443-448 —
      // `arr.push(setMinutes(setHours(day, h), 0));` + `setMinutes(setHours(day, h), 30)`
      // with no parameterisation or DST branches). Three sibling consumers
      // hardcode the same axis: useScheduleGridData.ts:348 (league-match
      // slotCount), :487 (training slotCount), :278+295 (overlap-check
      // addMinutes(slotStart, 30)). 30-min step is the canonical grid
      // resolution; ceil(duration/30) is the contract-correct slot count.
      const SLOT_STEP_MINUTES = 30;
      const slotCount = Math.max(1, Math.ceil(src.duration_minutes / SLOT_STEP_MINUTES));
      const resolution = resolveAndRouteMove({
        booking: src,
        targetCourtId: courtId,
        targetSlotIdx: slotIdx,
        targetSlotStart: slot,
        slotCount,
        totalSlotsInDay: timeSlots.length,
        bookings: bookings.map((b) => ({
          court_id: b.court_id,
          start_time: b.start_time,
          end_time: b.end_time,
          status: b.status,
          id: b.id,
        })),
        getLinkedLeagueMatch,
        getLinkedOpenMatch,
        getBookingCategory,
      });
      if (resolution.kind === "blocked") {
        // Stay in click-to-move mode so operator can pick another slot
        // without re-opening the panel. Toast surfaces the rejection reason.
        applyMoveResolution(resolution, src);
        return;
      }
      // Non-blocked → exit mode + route via the same setters as drag.
      setClickToMoveBooking(null);
      applyMoveResolution(resolution, src);
      return;
    }

    if (existingBooking) {
      // activity-click-fix — same routing as cell click.
      routeBookingClick(existingBooking as ScheduleBooking);
      return;
    }
    // PR-D Layer 1 (TASK-75): clicking a league_match slot shouldn't open
    // the booking selection rectangle. Tidsbokning Phase A: silent bail
    // (was: toast.info). The Seriespel overlay handles its own click →
    // MatchDetailModal; the slot-click event under it is a no-op.
    if (getLeagueMatchAt(courtId, slot)) {
      return;
    }
    setSelectionRect({ startCourtIdx: courtIdx, endCourtIdx: courtIdx, startSlotIdx: slotIdx, endSlotIdx: slotIdx });
  }, [courts, timeSlots, getBookingAt, getLeagueMatchAt, routeBookingClick, clickToMoveBooking, pricingRules, hallId, t, bookings, getLinkedLeagueMatch, getLinkedOpenMatch, getBookingCategory, applyMoveResolution]);

  const clearSelection = useCallback(() => setSelectionRect(null), []);

  // --- Booking drag-to-move handlers ---
  const handleBookingDragStart = useCallback((booking: ScheduleBooking, courtIdx: number, slotIdx: number, slotCount: number, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // overlap-defense Fix C — block drag-start during a refetch.
    // Prevents the React Query cache-stale validation window that
    // caused the operator's 5-rapid-move incident.
    if (isRescheduleInFlight) {
      toast.info(t("scheduleGrid.rescheduleBusy", { defaultValue: "Flyttar… vänta tills föregående flytt är klar." }));
      return;
    }
    bookingDragStartRef.current = { x: e.clientX, y: e.clientY, booking, courtIdx, slotIdx, slotCount };
  }, [isRescheduleInFlight, t]);

  // --- Tidsbokning Phase D — Seriespel drag-to-reschedule handlers ---
  // Mirrors the booking drag pattern. Only the pointer + match metadata
  // are stashed in the ref here; the matchDrag state is activated by the
  // global pointermove handler once MIN_DRAG_DISTANCE is exceeded.
  const handleMatchDragStart = useCallback(
    (matchId: string, courtIdx: number, slotIdx: number, slotCount: number, durationMinutes: number, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // overlap-defense Fix C — block match drag-start during refetch.
      if (isRescheduleInFlight) {
        toast.info(t("scheduleGrid.rescheduleBusy", { defaultValue: "Flyttar… vänta tills föregående flytt är klar." }));
        return;
      }
      matchDragStartRef.current = { x: e.clientX, y: e.clientY, matchId, courtIdx, slotIdx, slotCount, durationMinutes };
    },
    [isRescheduleInFlight, t],
  );

  // (Dead `_handleBookingDragMove` callback removed in UPPDRAG-31 —
  // target updates happen in the global pointermove handler below.)

  // UPPDRAG-31: drag-drop now ALWAYS opens MoveBookingDialog (or the
  // SubscriptionMoveDialog → MoveBookingDialog chain) with the drop
  // target pre-filled. The admin confirms via the dialog's "Flytta
  // bokning" button — no direct RPC call from here, no undo toast.
  // This also incidentally fixes the silent price-not-updated bug from
  // UPPDRAG-26 (the per-RPC fix shipped in this same PR via the
  // migration that aligns the RPC signature with what the dialog
  // sends).
  const handleBookingDrop = useCallback(async () => {
    if (!bookingDrag || !user) return;
    const { booking, originCourtIdx, originSlotIdx, targetCourtIdx, targetSlotIdx, slotCount } = bookingDrag;
    setBookingDrag(null);
    bookingDragStartRef.current = null;

    // Same position — no-op (UPPDRAG-29 dialog would also disable
    // submit if the drop equals the original, but skipping the dialog
    // entirely is a cleaner UX for the obvious "clicked but didn't
    // really drag" case).
    if (targetCourtIdx === originCourtIdx && targetSlotIdx === originSlotIdx) return;

    const targetCourtId = courts[targetCourtIdx]?.id;
    const targetSlot = timeSlots[targetSlotIdx];
    if (!targetCourtId || !targetSlot) return;

    // Lager 3a-fix — route through the canonical validate+pipeline
    // (extracted from this very function's pre-fix body). Click-to-move
    // calls the same function from handleSlotClick. resolveAndRouteMove
    // returns a MoveResolution discriminated union; applyMoveResolution
    // performs the setState/toast. Centralisation guarantees the two
    // paths can never disagree on validation or routing target.
    const resolution = resolveAndRouteMove({
      booking,
      targetCourtId,
      targetSlotIdx,
      targetSlotStart: targetSlot,
      slotCount,
      totalSlotsInDay: timeSlots.length,
      bookings: bookings.map((b) => ({
        court_id: b.court_id,
        start_time: b.start_time,
        end_time: b.end_time,
        status: b.status,
        id: b.id,
      })),
      getLinkedLeagueMatch,
      getLinkedOpenMatch,
      getBookingCategory,
    });
    applyMoveResolution(resolution, booking);
  }, [
    bookingDrag, courts, timeSlots, bookings, user,
    getBookingCategory, getLinkedLeagueMatch, getLinkedOpenMatch,
    applyMoveResolution,
  ]);

  // Tidsbokning Phase D — Seriespel drag drop handler. Opens the reschedule
  // dialog (via MatchDetailModal's auto-open path) with a pre-filled
  // initialTarget. Validation happens inside the dialog / RPC; the
  // green/red preview during drag is a hint, not a gate.
  const handleMatchDrop = useCallback(() => {
    if (!matchDrag) return;
    const targetCourtId = courts[matchDrag.targetCourtIdx]?.id;
    const targetSlot = timeSlots[matchDrag.targetSlotIdx];
    setMatchDrag(null);
    if (!targetCourtId || !targetSlot) return;
    // Hard-block on invalid (collision) — let user re-drag rather than open
    // a dialog they'll just cancel from.
    if (matchDrag.dropValid === false) {
      toast.info("Målplatsen är upptagen");
      return;
    }
    const dropTarget = resolveInitialTargetFromDrop({ targetCourtId, targetSlotStart: targetSlot });
    setMatchDropInitialTarget(dropTarget);
    setSelectedMatchId(matchDrag.matchId);
  }, [matchDrag, courts, timeSlots]);

  // Global pointer events for booking drag (and Tidsbokning Phase D match drag)
  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      // Booking drag path
      const start = bookingDragStartRef.current;
      if (start) {
        if (!bookingDrag) {
          const dist = Math.sqrt((e.clientX - start.x) ** 2 + (e.clientY - start.y) ** 2);
          if (dist < MIN_DRAG_DISTANCE) {
            // fall through to match-drag check below — booking ref is set but threshold not crossed
          } else {
            setBookingDrag({
              booking: start.booking,
              originCourtIdx: start.courtIdx,
              originSlotIdx: start.slotIdx,
              targetCourtIdx: start.courtIdx,
              targetSlotIdx: start.slotIdx,
              slotCount: start.slotCount,
              dropValid: null,
            });
          }
        }
      }
      // Tidsbokning Phase D — match drag activation (mirrors booking activation)
      const mstart = matchDragStartRef.current;
      if (mstart && !matchDrag) {
        const dist = Math.sqrt((e.clientX - mstart.x) ** 2 + (e.clientY - mstart.y) ** 2);
        if (dist >= MIN_DRAG_DISTANCE) {
          setMatchDrag({
            matchId: mstart.matchId,
            originCourtIdx: mstart.courtIdx,
            originSlotIdx: mstart.slotIdx,
            targetCourtIdx: mstart.courtIdx,
            targetSlotIdx: mstart.slotIdx,
            slotCount: mstart.slotCount,
            durationMinutes: mstart.durationMinutes,
            dropValid: null,
          });
        }
      }
      // No active drag — exit early after activation check
      if (!start && !mstart) return;
      if (!bookingDrag && !matchDrag && !matchDragStartRef.current && !bookingDragStartRef.current) return;

      // Map pointer to grid cell
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const relX = e.clientX - rect.left;
        const relY = e.clientY - rect.top;

        // Account for court label column (80px) and header row
        const headerHeight = 24; // approximate header height
        const cellWidth = (rect.width - 80) / timeSlots.length;
        const courtCount = courts.length;
        const rowH = (rect.height - headerHeight) / courtCount;

        const slotIdx = Math.max(0, Math.min(timeSlots.length - 1, Math.floor((relX - 80) / cellWidth)));
        const courtIdx = Math.max(0, Math.min(courtCount - 1, Math.floor((relY - headerHeight) / rowH)));

        // UPPDRAG-31 Bug 2: compute drop validity on every pointer move
        // so cells can paint green (valid) / red (invalid) while the
        // user is still hovering. Same helper as handleBookingDrop.
        setBookingDrag((prev) => {
          if (!prev) return null;
          const targetCourtId = courts[courtIdx]?.id;
          const targetSlotStart = timeSlots[slotIdx];
          let dropValid: boolean | null = null;
          if (targetCourtId && targetSlotStart) {
            const v = validateDropZone({
              targetCourtId,
              targetSlotIdx: slotIdx,
              slotCount: prev.slotCount,
              totalSlotsInDay: timeSlots.length,
              targetSlotStart,
              durationMinutes: prev.booking.duration_minutes,
              bookings: bookings.map(b => ({ court_id: b.court_id, start_time: b.start_time, end_time: b.end_time, status: b.status, id: b.id })),
              draggedBookingId: prev.booking.id,
            });
            dropValid = v.valid;
          }
          return { ...prev, targetCourtIdx: courtIdx, targetSlotIdx: slotIdx, dropValid };
        });
        // Tidsbokning Phase D — same dropValid computation for match drag.
        // (We don't have a draggedBookingId here; pass undefined so any
        // collision with bookings at the target slot flags red. League_match
        // overlaps are handled by trg_bookings_no_overlap server-side when
        // the RPC eventually fires move_booking; the visual hint is enough.)
        setMatchDrag((prev) => {
          if (!prev) return null;
          const targetCourtId = courts[courtIdx]?.id;
          const targetSlotStart = timeSlots[slotIdx];
          let dropValid: boolean | null = null;
          if (targetCourtId && targetSlotStart) {
            const v = validateDropZone({
              targetCourtId,
              targetSlotIdx: slotIdx,
              slotCount: prev.slotCount,
              totalSlotsInDay: timeSlots.length,
              targetSlotStart,
              durationMinutes: prev.durationMinutes,
              bookings: bookings.map(b => ({ court_id: b.court_id, start_time: b.start_time, end_time: b.end_time, status: b.status, id: b.id })),
            });
            dropValid = v.valid;
          }
          return { ...prev, targetCourtIdx: courtIdx, targetSlotIdx: slotIdx, dropValid };
        });
        setDragPointer({ x: e.clientX, y: e.clientY });
      }
    };

    const handlePointerUp = () => {
      setDragPointer(null);
      // Booking path
      if (bookingDragStartRef.current && !bookingDrag) {
        bookingDragStartRef.current = null;
      } else if (bookingDrag) {
        handleBookingDrop();
        bookingDragStartRef.current = null;
      }
      // Tidsbokning Phase D — match path
      if (matchDragStartRef.current && !matchDrag) {
        // Didn't reach min distance — let onClick (Phase A) fire instead
        matchDragStartRef.current = null;
      } else if (matchDrag) {
        handleMatchDrop();
        matchDragStartRef.current = null;
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
    // UPPDRAG-31: bookings is now a dep (used in validateDropZone).
    // Tidsbokning Phase D: matchDrag + handleMatchDrop added.
  }, [bookingDrag, timeSlots, courts, bookings, handleBookingDrop, matchDrag, handleMatchDrop]);

  const selectionInfo = useMemo(
    () => computeSelectionInfo(selectionRect),
    [computeSelectionInfo, selectionRect],
  );

  // Find blocked bookings within the current selection range
  const blockedInSelection = useMemo(() => {
    if (!selectionInfo) return [];
    const start = selectionInfo.startTime.getTime();
    const end = selectionInfo.endTime.getTime();
    return bookings.filter((b) => {
      if (b.status !== "blocked") return false;
      if (!selectionInfo.courtIds.includes(b.court_id)) return false;
      const bStart = new Date(b.start_time).getTime();
      const bEnd = new Date(b.end_time).getTime();
      return bStart < end && bEnd > start;
    });
  }, [selectionInfo, bookings]);

  const handleUnblockTime = async () => {
    if (blockedInSelection.length === 0) return;
    setIsBooking(true);
    let hasError = false;

    for (const b of blockedInSelection) {
      const { error } = await supabase
        .from("bookings")
        .delete()
        .eq("id", b.id);
      if (error) {
        toast.error(error.message);
        hasError = true;
        break;
      }
    }

    if (!hasError) {
      const count = blockedInSelection.length;
      toast.success(
        count === 1
          ? t("booking:unblockSuccess")
          : t("booking:unblockMultipleSuccess", { count })
      );
      invalidateBookings();
      clearSelection();
    }
    setIsBooking(false);
  };

  const handleBlockTime = async () => {
    if (!selectionInfo || !user) return;
    setIsBooking(true);
    let hasError = false;

    for (const cId of selectionInfo.courtIds) {
      // PR-AJ Layer B — pre-INSERT availability check for clean UX message
      // (DB trigger trg_bookings_no_overlap is the structural guarantee).
      const availability = await validateCourtAvailability(supabase, {
        courtId: cId,
        startTime: selectionInfo.startTime,
        endTime: selectionInfo.endTime,
      });
      if (!availability.available) {
        toast.error(t("scheduleGrid.couldNotBlock") + " — " + availability.message);
        hasError = true;
        break;
      }

      // Sprint D dual-write — blocked court (free, price=0).
      // PR-AN: created_by = admin (session user). For blocks user_id = admin too
      // since the block "owner" is the admin themselves; both fields align here.
      const { error } = await supabase.from("bookings").insert({
        court_id: cId,
        user_id: user.id,
        created_by: user.id,
        start_time: selectionInfo.startTime.toISOString(),
        end_time: selectionInfo.endTime.toISOString(),
        duration_minutes: selectionInfo.durationMinutes,
        status: "blocked" as Tables<"bookings">["status"],
        info_text: t("scheduleGrid.blockedByAdmin"),
        price_per_hour: 0,
        price_per_hour_minor: 0,
        price_total: 0,
        price_total_minor: 0,
        currency: "SEK",
      });
      if (error) {
        toast.error(t("scheduleGrid.couldNotBlock") + " " + error.message);
        hasError = true;
        break;
      }
    }

    if (!hasError) {
      toast.success(t("scheduleGrid.courtsBlocked", { count: selectionInfo.courtIds.length }));
      invalidateBookings();
      clearSelection();
    }
    setIsBooking(false);
  };

  const filteredBookings = searchQuery
    ? bookings.filter((b) => {
        const name = getPlayerName(b).toLowerCase();
        return name.includes(searchQuery.toLowerCase());
      })
    : null;

  // PR-AK — Branched cell-click handler: in multi-select mode, toggles
  // selection of confirmed bookings; otherwise opens BookingDetailModal.
  // Hooks MUST live above the early-return below (Rules of Hooks — fixed
  // post-PR-AK after a React #310 incident in prod).
  const handleBookingClickGrid = useCallback((booking: ScheduleBooking) => {
    if (multiSelectMode) {
      if (booking.status === "confirmed") {
        toggleBookingSelection(booking.id);
      }
      // Non-confirmed (blocked/cancelled) silently ignored in select mode.
      return;
    }
    routeBookingClick(booking);
    // routeBookingClick is declared above handleSlotClick (which is
    // earlier in the file) — so the reference here is safe at runtime
    // and the closure dep is identity-stable.
  }, [multiSelectMode, toggleBookingSelection, routeBookingClick]);

  // PR-AK — Toolbar mode toggle handler. When turning OFF, clear selection.
  const handleToggleMultiSelect = useCallback(() => {
    setMultiSelectMode((prev) => {
      const next = !prev;
      if (!next) setSelectedBookingIds(new Set());
      return next;
    });
  }, []);

  if (!hallHours) {
    return <div className="p-4 text-sm text-muted-foreground">{t("scheduleGrid.loadingHours")}</div>;
  }

  // Handle crediting when a released subscription slot gets booked
  const handleReleaseCredit = async (
    release: { id: string; subscription_id: string; original_price: number; released_by: string; court_id: string },
    bookingData: Record<string, unknown>,
  ) => {
    if (!organization?.id) return;
    const rules = (organization as any)?.settings?.subscription_release_rules;
    if (!rules?.enabled) return;

    // Check eligible booking types
    if (rules.eligible_booking_types === "revenue_only") {
      const paymentMethod = bookingData.paymentMethod as string;
      if (paymentMethod === "free" || bookingData.type === "block") return;
      const price = (bookingData.finalPrice as number) ?? 0;
      if (price <= 0) return;
    }

    // Calculate credit amount
    let creditAmount = 0;
    if (rules.credit_mode === "percentage") {
      creditAmount = Math.round((release.original_price * (rules.credit_percentage || 0)) / 100);
    } else if (rules.credit_mode === "actual_revenue") {
      let amount = (bookingData.finalPrice as number) ?? 0;
      // Apply cancellation fee (club takes a cut)
      const feeType = rules.cancellation_fee_type || "none";
      const feeValue = rules.cancellation_fee_value || 0;
      if (feeType === "percentage") {
        amount = amount * (1 - feeValue / 100);
      } else if (feeType === "fixed") {
        amount = amount - feeValue;
      }
      // Apply cap at original subscription price
      if (rules.cap_at_original_price) {
        amount = Math.min(amount, release.original_price || 0);
      }
      creditAmount = Math.max(0, Math.round(amount));
    }
    if (creditAmount <= 0) return;

    // Calculate expiry
    const validityDays = rules.credit_validity_days || 0;
    const expiresAt = validityDays > 0
      ? new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000).toISOString()
      : null;

    // Find subscription holder
    const { data: sub } = await supabase.from("recurring_bookings").select("user_id, customer_id").eq("id", release.subscription_id).single();
    const holderUserId = (sub as any)?.user_id;
    if (!holderUserId) return;

    // Sprint D dual-write: include amount + amount_minor + currency.
    const creditAmountMinor = Math.round(creditAmount * 100);
    const { data: creditTx, error: creditError } = await supabase
      .from("credit_transactions" as any)
      .insert({
        user_id: holderUserId,
        organization_id: organization.id,
        amount: creditAmount,                      // legacy kronor
        amount_minor: creditAmountMinor,           // öre
        currency: "SEK",
        type: "release_credit",
        reference_id: release.id,
        description: t("scheduleGrid.releaseCreditDesc"),
        source: "system",
        expires_at: expiresAt,
      })
      .select("id")
      .single();
    if (creditError) return;

    // Sprint D dual-state: prefer balance_minor, fall back to legacy.
    const { data: existing } = await supabase
      .from("player_credits" as any)
      .select("balance, balance_minor, currency")
      .eq("user_id", holderUserId)
      .eq("organization_id", organization.id)
      .maybeSingle();

    const existingMinor = (existing as { balance_minor?: number | null } | null)?.balance_minor;
    const existingLegacy = (existing as { balance?: number } | null)?.balance;
    const currentMinor =
      typeof existingMinor === "number" ? existingMinor :
      typeof existingLegacy === "number" ? existingLegacy * 100 : 0;
    const newBalanceMinor = currentMinor + creditAmountMinor;
    const newBalanceKronor = Math.round(newBalanceMinor / 100);

    if (existing) {
      await supabase.from("player_credits" as any)
        .update({
          balance: newBalanceKronor,
          balance_minor: newBalanceMinor,
          currency: "SEK",
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", holderUserId).eq("organization_id", organization.id);
    } else {
      await supabase.from("player_credits" as any)
        .insert({
          user_id: holderUserId,
          organization_id: organization.id,
          balance: newBalanceKronor,
          balance_minor: newBalanceMinor,
          currency: "SEK",
        });
    }

    // Update release record
    await supabase.from("subscription_releases" as any)
      .update({ status: "rebooked", rebooked_at: new Date().toISOString(), credit_amount: creditAmount, credit_transaction_id: (creditTx as any)?.id || null })
      .eq("id", release.id);

    toast.info(t("scheduleGrid.releaseCredited", { amount: creditAmount }));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Lager 3a — click-to-move active banner. Sticky so it stays visible
          while operator scans for a target slot. Esc clears via the keydown
          listener above; clicking on a booking clears + routes; clicking on
          an empty slot opens MoveBookingDialog (if same-price) or warns. */}
      {clickToMoveBooking && (
        <div
          className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-sm flex items-center justify-between gap-3 sticky top-0 z-30"
          data-testid="click-to-move-banner"
        >
          <span>
            {t("scheduleGrid.moveBannerActive", {
              defaultValue:
                "Flyttar bokning ({{time}}). Klicka på en ledig tid med samma pris för att flytta — Esc avbryter.",
              time: format(new Date(clickToMoveBooking.start_time), "HH:mm"),
            })}
          </span>
          <button
            type="button"
            onClick={() => {
              setClickToMoveBooking(null);
              toast.info(t("scheduleGrid.moveCancelled", { defaultValue: "Flytt avbruten." }));
            }}
            className="text-amber-900 underline text-xs"
          >
            {t("common:cancel", { defaultValue: "Avbryt" })}
          </button>
        </div>
      )}
      <ScheduleGridToolbar
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        clearSelection={clearSelection}
        onCreateBooking={() => {
          setBookingDialog(true);
        }}
        selectionInfo={selectionInfo}
        onBlockTime={handleBlockTime}
        onUnblockTime={handleUnblockTime}
        blockedCount={blockedInSelection.length}
        isBooking={isBooking}
        filteredBookings={filteredBookings}
        getPlayerName={getPlayerName}
        // activity-click-fix — route activity-linked bookings to
        // ActivityDetailModal here too (toolbar search-result click).
        onBookingClick={routeBookingClick}
        onBookFromSelection={() => {
          setBookingDialog(true);
        }}
        multiSelectMode={multiSelectMode}
        onToggleMultiSelect={handleToggleMultiSelect}
        selectedBookingCount={selectedBookingIds.size}
      />

      {/* CSS Grid schedule – fills available space, scrollable on mobile */}
      <div ref={containerRef} className={cn("w-full relative flex-1 min-h-0 overflow-x-auto", (isDragging || bookingDrag) && "select-none")}>
        <ScheduleGridHeader timeSlots={timeSlots} />

        {/* Court rows – dynamic height */}
        {courts.map((court, courtIdx) => (
          <ScheduleGridRow
            key={court.id}
            court={court}
            courtIdx={courtIdx}
            timeSlots={timeSlots}
            rowHeight={rowHeight}
            bookings={bookings}
            selectionRect={selectionRect}
            courts={courts}
            hall={hall as Tables<"halls"> | null}
            catByUser={catByUser}
            getBookingCategory={getBookingCategory}
            getPlayerName={getPlayerName}
            getActivityLabel={getActivityLabel}
            getLeagueMatchLabel={getLeagueMatchLabel}
            getTrainingSlotInfo={getTrainingSlotInfo}
            getLeagueMatchSlotInfo={getLeagueMatchSlotInfo}
            onMatchClick={(matchId) => {
              // Click path (no drag) — open detail modal, no pre-fill.
              setMatchDropInitialTarget(null);
              setSelectedMatchId(matchId);
            }}
            onMatchDragStart={handleMatchDragStart}
            matchDrag={matchDrag}
            onBookingClick={handleBookingClickGrid}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onSlotClick={handleSlotClick}
            dragRef={dragRef}
            bookingDrag={bookingDrag}
            onBookingDragStart={handleBookingDragStart}
            clickToMoveBooking={clickToMoveBooking}
            getRecurringBlockAt={getRecurringBlockAt}
            getRecurringBookingAt={getRecurringBookingAt}
            getSubscriptionReleaseAt={getSubscriptionReleaseAt}
            rbCustomerMap={rbCustomerMap}
            onRecurringBlockClick={setSelectedRecurringBlock}
            onSubscriptionClick={setSelectedSubscription}
            highlighted={highlightedCourtIds.includes(court.id)}
            multiSelectMode={multiSelectMode}
            bulkSelectedBookingIds={selectedBookingIds}
          />
        ))}

        {/* Now indicator */}
        {nowPosition !== null && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-destructive z-20 pointer-events-none"
            style={{ left: `calc(${(80 * (1 - nowPosition / 100)).toFixed(2)}px + ${nowPosition.toFixed(2)}%)` }}
          >
            <div className="w-2 h-2 rounded-full bg-destructive -translate-x-[3px] -translate-y-0.5" />
          </div>
        )}

        {/* Gap 9: Ghost element during drag */}
        {bookingDrag && dragPointer && (
          <div
            className="fixed z-50 pointer-events-none bg-primary/20 border border-primary rounded-sm px-2 py-1 text-[10px] font-medium shadow-lg"
            style={{ left: dragPointer.x + 12, top: dragPointer.y - 10 }}
          >
            {getPlayerName(bookingDrag.booking)} &middot; {format(new Date(bookingDrag.booking.start_time), "HH:mm")}
          </div>
        )}
      </div>

      {/* FIX #4: KPI summary panel */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="bg-card border border-border rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-center">
          <p className="text-xl sm:text-2xl font-bold text-foreground">{kpis.bookingCount}</p>
          <p className="text-[10px] sm:text-xs text-muted-foreground">{t("scheduleGrid.bookingsToday")}</p>
        </div>
        <div className="bg-card border border-border rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-center">
          <p className="text-xl sm:text-2xl font-bold text-foreground">{kpis.occupancy}%</p>
          <p className="text-[10px] sm:text-xs text-muted-foreground">{t("scheduleGrid.occupancy")}</p>
        </div>
        <div className="bg-card border border-border rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-center">
          <p className="text-xl sm:text-2xl font-bold text-foreground">{formatPrice(kpis.actualRevenue + kpis.estimatedBlockRevenue)}</p>
          {kpis.standardRevenue !== kpis.actualRevenue && (
            <p className="text-[10px] text-muted-foreground line-through">{formatPrice(kpis.standardRevenue)} {t("scheduleGrid.standardPrice")}</p>
          )}
          {(kpis.confirmedBlockRevenue > 0 || kpis.estimatedBlockRevenue > 0) && (
            <p className="text-[10px] text-muted-foreground">
              {kpis.confirmedBlockRevenue > 0 && <span className="text-green-600">{formatPrice(kpis.confirmedBlockRevenue)} {t("scheduleGrid.confirmed")}</span>}
              {kpis.confirmedBlockRevenue > 0 && kpis.estimatedBlockRevenue > 0 && " · "}
              {kpis.estimatedBlockRevenue > 0 && <span className="text-amber-600">{formatPrice(kpis.estimatedBlockRevenue)} {t("scheduleGrid.estimated")}</span>}
            </p>
          )}
          <p className="text-[10px] sm:text-xs text-muted-foreground">{t("scheduleGrid.revenueToday")}</p>
        </div>
        <div className="bg-card border border-border rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-center">
          <p className="text-xl sm:text-2xl font-bold text-foreground">{kpis.freeHours}</p>
          <p className="text-[10px] sm:text-xs text-muted-foreground">{t("scheduleGrid.freeHours")}</p>
        </div>
      </div>

      {/* Legend — horizontal scroll on mobile, flex-wrap on desktop */}
      <div className="mt-2 flex sm:flex-wrap sm:justify-center gap-2 sm:gap-3 text-[10px] sm:text-xs pb-2 overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-1 shrink-0">
          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-court-free/20 border border-court-free/30" />
          <span>{t("scheduleGrid.free")}</span>
        </div>
        {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
          <div key={key} className="flex items-center gap-1 shrink-0">
            <div className={cn("w-2.5 h-2.5 sm:w-3 sm:h-3 rounded border", style.bg, style.border)} />
            <span>{style.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1 shrink-0">
          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded border-2 border-dashed border-primary/60 bg-primary/10" />
          <span>{t("scheduleGrid.training")}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-primary/30 ring-1 ring-primary/40" />
          <span>{t("scheduleGrid.selected")}</span>
        </div>
      </div>

      {/* Lager 1 — read-only booking detail panel (replaces the previous
          BookingDetailModal entry from grid click flow). Drag-to-move via
          MoveBookingDialog (UPPDRAG-31) remains the mutation entry.
          Lager 3a — onMoveBooking activates click-to-move mode + closes
          the panel; the grid then routes the next empty-slot click as
          a drop target via handleSlotClick. */}
      <BookingDetailPanel
        open={!!selectedBookingPanel}
        onOpenChange={(v) => { if (!v) setSelectedBookingPanel(null); }}
        booking={selectedBookingPanel}
        onMoveBooking={activateClickToMove}
      />

      {/* Recurring block detail dialog */}
      {selectedRecurringBlock && (
        <RecurringBlockDetailDialog
          block={selectedRecurringBlock}
          open={!!selectedRecurringBlock}
          onOpenChange={(v) => { if (!v) setSelectedRecurringBlock(null); }}
          selectedDate={selectedDate}
          courts={courts.map((c) => ({ id: c.id, name: c.name }))}
          onUpdated={() => {
            invalidateBookings();
            setSelectedRecurringBlock(null);
          }}
          onHighlightCourts={setHighlightedCourtIds}
        />
      )}

      {/* Tidsbokning Phase A — Seriespel match detail modal.
          Phase D: when initialRescheduleTarget is set (from a drag-drop),
          the modal auto-opens the reschedule dialog with the TILL fields
          pre-filled. Click-path keeps initialRescheduleTarget null and the
          modal stays in read-only detail mode until user clicks "Flytta". */}
      {selectedMatchId && (
        <MatchDetailModal
          matchId={selectedMatchId}
          open={!!selectedMatchId}
          onOpenChange={(v) => {
            if (!v) {
              setSelectedMatchId(null);
              setMatchDropInitialTarget(null);
            }
          }}
          initialRescheduleTarget={matchDropInitialTarget}
          onRescheduled={() => {
            // Refresh timeline data so the moved match renders at its new slot.
            invalidateBookings();
            setSelectedMatchId(null);
            setMatchDropInitialTarget(null);
          }}
        />
      )}

      {/* activity-click-fix — Activity detail modal for admin-created
          open_matches that have a linked blocking booking on the schedule.
          Reuses useActivityDetail so the data shape stays canonical
          across player + admin surfaces. */}
      {selectedActivityId && (
        <ActivityDetailModal
          activityId={selectedActivityId}
          open={!!selectedActivityId}
          onOpenChange={(v) => {
            if (!v) {
              setSelectedActivityId(null);
              setActivityDropInitialTarget(null);
            }
          }}
          initialRescheduleTarget={activityDropInitialTarget}
          onRescheduled={() => {
            invalidateBookings();
            setSelectedActivityId(null);
            setActivityDropInitialTarget(null);
          }}
        />
      )}

      {/* Subscription detail dialog */}
      {selectedSubscription && (
        <SubscriptionDetailDialog
          subscription={selectedSubscription}
          open={!!selectedSubscription}
          onOpenChange={(v) => { if (!v) setSelectedSubscription(null); }}
          courts={courts.map((c) => ({ id: c.id, name: c.name }))}
          onUpdated={() => {
            invalidateBookings();
            setSelectedSubscription(null);
          }}
          customerName={rbCustomerMap.get(selectedSubscription.customer_id)}
          organizationId={organization?.id}
          selectedDate={selectedDate}
        />
      )}

      {/* Move booking dialog */}
      {moveBooking && hallHours && hallId && (
        <MoveBookingDialog
          open={!!moveBooking}
          // UPPDRAG-31: clear the drop target alongside the booking so a
          // subsequent menu-opened move starts with empty fields again.
          onOpenChange={(v) => { if (!v) { setMoveBooking(null); setMoveDropTarget(null); } }}
          booking={{
            id: moveBooking.id,
            court_id: moveBooking.court_id,
            court_name: moveBooking.courts?.name ?? t("scheduleGrid.court"),
            start_time: moveBooking.start_time,
            end_time: moveBooking.end_time,
            duration_minutes: moveBooking.duration_minutes,
            price_total: moveBooking.price_total,
            price_total_minor: (moveBooking as any).price_total_minor ?? null,
            currency: (moveBooking as any).currency ?? "SEK",
            user_id: moveBooking.user_id,
            booker_name: moveBooking.profiles?.full_name ?? t("common.unknown"),
            status: moveBooking.status,
            // Lager 3b.1 (P3-fix): krävs för MoveBookingDialog att branch:a
            // per-seat (private) vs flat-standard (open-match).
            is_open_match: (moveBooking as any).is_open_match ?? false,
            activity_id: (moveBooking as any).activity_id ?? null,
          }}
          // Lager 3b.1: max_players krävs för per-seat-uträkning (seatCount)
          courts={courts.map(c => ({ id: c.id, name: c.name, max_players: (c as any).max_players ?? 4 }))}
          existingBookings={(bookings ?? []).map((b: ScheduleBooking) => ({
            court_id: b.court_id,
            start_time: b.start_time,
            end_time: b.end_time,
            status: b.status,
            id: b.id,
          }))}
          hallHours={hallHours}
          hallId={hallId}
          pricingRules={pricingRules}
          timeStep={(hall as Tables<"halls"> | null)?.time_step_minutes ?? 15}
          adminId={user?.id ?? ""}
          // UPPDRAG-31: pre-fill from drag-drop (null when opened from menu)
          initialTarget={moveDropTarget}
          onMoved={() => {
            invalidateBookings();
            setMoveBooking(null);
            setMoveDropTarget(null);
          }}
        />
      )}

      {/* Subscription/activity move choice dialog */}
      {subscriptionMoveBooking && (
        <SubscriptionMoveDialog
          open={!!subscriptionMoveBooking}
          onOpenChange={(v) => { if (!v) setSubscriptionMoveBooking(null); }}
          booking={subscriptionMoveBooking.booking}
          bookingType={subscriptionMoveBooking.type}
          activityDetails={
            subscriptionMoveBooking.type === "activity" && subscriptionMoveBooking.booking.activity_id
              ? { title: (subscriptionMoveBooking.booking as any).activity_title ?? undefined }
              : undefined
          }
          onChoice={async (choice) => {
            const bk = subscriptionMoveBooking.booking;
            if (choice === "single" && subscriptionMoveBooking.type === "recurring") {
              // Gap 1: Create subscription exception
              const bStart = new Date(bk.start_time);
              const timeStr = format(bStart, "HH:mm:ss");
              const matchingRecurring = recurringBookings.find(
                (r: any) => r.court_id === bk.court_id && r.start_time === timeStr,
              );
              if (matchingRecurring) {
                await supabase.from("subscription_exceptions" as any).insert({
                  subscription_id: (matchingRecurring as any).id,
                  original_date: format(bStart, "yyyy-MM-dd"),
                  exception_type: "moved",
                  replacement_booking_id: bk.id,
                  created_by: user?.id,
                }).catch(() => {});
              }
            }
            if (choice === "single" || choice === "all_future") {
              setMoveBooking(bk);
            }
            setSubscriptionMoveBooking(null);
          }}
        />
      )}

      {/* Admin booking sheet */}
      <AdminBookingSheet
        open={bookingDialog}
        onOpenChange={(v) => { if (!v) { setBookingDialog(false); } }}
        courtIds={selectionInfo?.courtIds ?? []}
        courtNames={selectionInfo?.courtNames ?? []}
        startTime={selectionInfo?.startTime ?? null}
        endTime={selectionInfo?.endTime ?? null}
        courts={courts}
        organizationId={organization?.id ?? null}
        hallId={hallId}
        pricingRules={pricingRules}
        isBooking={isBooking}
        onHighlightCourts={setHighlightedCourtIds}
        selectedDate={selectedDate}
        onConfirm={async (data) => {
          if (data.type === "court") {
            if (!selectionInfo || !user) return;
            setIsBooking(true);
            // PR-AL: walk-in attribution. When admin leaves customer empty
            // (intended walk-in), use the org's sentinel walk_in_user_id
            // instead of falling back to the admin's own user.id — that
            // was polluting "top players" reports with admin-attributed
            // walk-in bookings. The walk-in user is non-loginable; its
            // profile renders as "Walk-in" in admin views.
            const bookingUserId =
              data.customerUserId ??
              (organization as any)?.walk_in_user_id ??
              user.id;
            const courtIdsToBook = selectionInfo.courtIds;
            const courtCount = courtIdsToBook.length;

            // Path B+ — resolve booker category för per-seat-pris.
            // Async submit-time resolution (Q6 fallback — pre-fetch i form-
            // state är preferred men kräver AdminBookingSheet-utvidgning;
            // submit-time räcker för fix av M1-buggen). getPlayerCategory
            // fallbackar till 'standard' om ingen membership/customer-row
            // matchar (icke-medlem eller walk-in).
            const bookerCat = organization?.id
              ? await getPlayerCategory(bookingUserId, organization.id)
              : { slug: "standard" as string, dailyMinutesLimit: null, fallbackCategory: "standard", overflowPercent: 100 };

            let hasError = false;
            for (const cId of courtIdsToBook) {
              // PR-AJ Layer B — pre-INSERT availability check.
              const availability = await validateCourtAvailability(supabase, {
                courtId: cId,
                startTime: selectionInfo.startTime,
                endTime: selectionInfo.endTime,
              });
              if (!availability.available) {
                toast.error(t("scheduleGrid.couldNotBook") + " — " + availability.message);
                hasError = true;
                break;
              }

              // Path B+ — canonical per-court pris via resolveBookingTargetPrice
              // (Lesson 42: kontext-agnostisk helper, single source of truth per
              // Lesson 38). Admin-direct booking är ALLTID private (open-match-
              // grenen är separat code-path nedan). Per-seat-formel triggas
              // automatiskt för member-bookers; F5-guard preserverar exakt
              // full court-pris för standard-only-bokningar.
              const courtSeatCount =
                courts.find((c) => c.id === cId)?.max_players ?? 4;
              const canonicalTarget = resolveBookingTargetPrice({
                bookingKind: "private",
                targetSlot: {
                  hallId,
                  startTime: selectionInfo.startTime,
                  durationMinutes: selectionInfo.durationMinutes,
                  seatCount: courtSeatCount,
                  pricingRules,
                  specialDates: [],
                },
                bookerCategorySlug: bookerCat.slug,
                invitedCategorySlugs: [],
              });

              // Admin-override-prioritet: customPrice > free > canonical.
              // Sprint D dual-write — bevara öre-exakt precision via _minor.
              const canonicalMinor = canonicalTarget.totalMinor;
              const perCourtPriceMinor = data.customPrice != null
                ? Math.round(data.customPrice * 100)
                : data.paymentMethod === "free"
                  ? 0
                  : canonicalMinor;
              const perCourtPrice = Math.round(perCourtPriceMinor / 100);
              // standard_price_total reflekterar listpris (= "vad det skulle
              // kostat utan medlemsrabatt") — använd resolverns F5-output med
              // bookerCategory='standard' för att få samma slot-pris men utan
              // member-rabatt. För admin-flow är det förenklat = canonicalMinor
              // när bookerCat='standard', annars data.standardPrice (form-pris).
              const perCourtStandardPrice = data.standardPrice ?? perCourtPrice;
              // Sprint D dual-write — booking creation across all variants.
              // PR-AN: created_by = admin (session user) so "Bokad av" renders
              // the actual creator, not the customer/walk-in user_id.
              const insertData: TablesInsert<"bookings"> = {
                court_id: cId,
                user_id: bookingUserId,
                created_by: user.id,
                start_time: selectionInfo.startTime.toISOString(),
                end_time: selectionInfo.endTime.toISOString(),
                duration_minutes: selectionInfo.durationMinutes,
                price_total: perCourtPrice,
                price_total_minor: perCourtPriceMinor,
                standard_price_total: perCourtStandardPrice,
                standard_price_total_minor: Math.round(perCourtStandardPrice * 100),
                currency: "SEK",
                ...(data.customerId ? { customer_id: data.customerId } as any : {}),
                ...(selectionInfo.price && selectionInfo.price.perHour !== null ? {
                  price_per_hour: selectionInfo.price.perHour,
                  price_per_hour_minor: Math.round(selectionInfo.price.perHour * 100),
                } : {}),
                ...(data.customPrice != null ? {
                  custom_price: data.customPrice,
                  custom_price_minor: Math.round(data.customPrice * 100),
                  custom_price_note: data.customPriceNote ?? null,
                } : {}),
                ...(data.comment ? { notes: data.comment } : {}),
                // Free booking fields
                ...(data.paymentMethod === "free" ? {
                  price_total: 0,
                  price_total_minor: 0,
                  free_reason: data.freeReason ?? null,
                  free_reason_text: data.freeReasonText ?? null,
                } as any : {}),
                // Invoice booking
                ...(data.paymentMethod === "invoice" ? {
                  invoice_status: "pending",
                } as any : {}),
              };
              const { data: insertedBooking, error } = await supabase.from("bookings").insert(insertData).select("id").single();
              if (error) {
                toast.error(t("scheduleGrid.couldNotBook") + " " + error.message);
                hasError = true;
                break;
              }
              // Create payment record for "paid_cash_swish" — Sprint D dual-write
              if (data.paymentMethod === "paid_cash_swish" && insertedBooking) {
                await supabase.from("booking_payments").insert({
                  booking_id: insertedBooking.id,
                  user_id: bookingUserId,
                  amount: perCourtPrice,                              // legacy kronor
                  amount_minor: Math.round(perCourtPrice * 100),      // öre
                  currency: "SEK",
                  payment_type: "full" as const,
                  status: "manual" as const,
                  payment_method: "cash",
                  paid_at: new Date().toISOString(),
                } as any).catch(() => {});
              }
              // Klippkort payment — deduct 1 clip and mark as paid
              if (data.paymentMethod === "credits" && insertedBooking) {
                const punchCardId = data.selectedPunchCardId as string | undefined;
                if (punchCardId) {
                  // Fetch card, deduct 1 punch.
                  // Sprint D PR-AC Phase 4-aware: include price_paid_minor + currency
                  // in SELECT; prefer _minor (öre / total → öre per clip → kr) over legacy.
                  const { data: card } = await supabase
                    .from("punch_cards" as any)
                    .select("id, name, total_punches, used_punches, price_paid, price_paid_minor, currency")
                    .eq("id", punchCardId)
                    .single();
                  if (card) {
                    const newUsed = (card as any).used_punches + 1;
                    // Phase 4-aware per-clip value calculation.
                    const totalPunches = (card as any).total_punches ?? 1;
                    const perClipValue = (card as any).price_paid_minor != null
                      ? Math.round(((card as any).price_paid_minor / totalPunches) / 100)
                      : Math.round(((card as any).price_paid ?? 0) / totalPunches);
                    const updates: Record<string, unknown> = { used_punches: newUsed };
                    if (newUsed >= (card as any).total_punches) updates.status = "completed";
                    await supabase.from("punch_cards" as any).update(updates).eq("id", punchCardId);
                    // Create paid payment record with per-clip revenue value — Sprint D dual-write
                    await supabase.from("booking_payments").insert({
                      booking_id: insertedBooking.id,
                      user_id: bookingUserId,
                      amount: perClipValue,                              // legacy kronor
                      amount_minor: Math.round(perClipValue * 100),      // öre
                      currency: "SEK",
                      payment_type: "full" as const,
                      status: "paid" as const,
                      payment_method: "credits",
                      paid_at: new Date().toISOString(),
                    } as any).catch(() => {});
                    // Log transaction — Sprint D: amount=0 means amount_minor=0.
                    await supabase.from("credit_transactions" as any).insert({
                      user_id: bookingUserId,
                      organization_id: organization?.id,
                      amount: 0,
                      amount_minor: 0,
                      currency: "SEK",
                      type: "punch_card_use",
                      description: t("scheduleGrid.clipUsedViaBooking", { name: (card as any).name }),
                      reference_id: insertedBooking.id,
                      admin_user_id: user.id,
                    }).catch(() => {});
                  }
                }
              }
            }
            if (!hasError) {
              // Check for released subscription slots that this booking fills
              for (const cId of courtIdsToBook) {
                try {
                  const bookingDateStr = format(selectionInfo.startTime, "yyyy-MM-dd");
                  const bookingStartTime = format(selectionInfo.startTime, "HH:mm:ss");
                  const bookingEndTime = format(selectionInfo.endTime, "HH:mm:ss");
                  const { data: releases } = await supabase
                    .from("subscription_releases" as any)
                    .select("id, subscription_id, original_price, released_by, court_id, start_time, end_time")
                    .eq("court_id", cId)
                    .eq("release_date", bookingDateStr)
                    .eq("status", "released")
                    .lte("start_time", bookingStartTime)
                    .gte("end_time", bookingEndTime);
                  if (releases && releases.length > 0) {
                    for (const release of releases) {
                      await handleReleaseCredit(release as any, data);
                    }
                  }
                } catch (err) {
                  console.error("Release credit check failed:", err);
                }
              }
              toast.success(t("scheduleGrid.bookingCreated", { count: courtIdsToBook.length }));
              invalidateBookings();
              clearSelection();
            }
            setIsBooking(false);
            setBookingDialog(false);
          } else if (data.type === "block") {
            if (!selectionInfo || !user) return;
            setIsBooking(true);
            let hasError = false;
            for (const cId of selectionInfo.courtIds) {
              // PR-AN: blocks created via wizard — user_id and created_by both = admin.
              const { error } = await supabase.from("bookings").insert({
                court_id: cId,
                user_id: user.id,
                created_by: user.id,
                start_time: selectionInfo.startTime.toISOString(),
                end_time: selectionInfo.endTime.toISOString(),
                duration_minutes: selectionInfo.durationMinutes,
                status: "blocked" as Tables<"bookings">["status"],
                info_text: data.description || data.reason,
              });
              if (error) { toast.error(t("scheduleGrid.couldNotBlock") + " " + error.message); hasError = true; break; }
            }
            if (!hasError) {
              toast.success(t("scheduleGrid.timeBlocked"));
              invalidateBookings();
              clearSelection();
            }
            setIsBooking(false);
            setBookingDialog(false);
          } else if (data.type === "open_match") {
            if (!selectionInfo || !user || !organization) return;
            setIsBooking(true);
            try {
              const courtId = (data.courtId as string) ?? selectionInfo.courtIds[0];
              const startTimeStr = format(selectionInfo.startTime, "HH:mm:ss");
              const endTimeStr = format(selectionInfo.endTime, "HH:mm:ss");
              const maxPlayers = (data.matchType as string) === "single" ? 2 : 4;
              const selectedPlayers = (data.selectedPlayers as string[] | undefined) ?? [];
              const recurring = data.recurring as { enabled: boolean; days: number[]; weeks: number } | undefined;

              // T2v2/G-010/G-013: the grid's open_match creation goes through the
              // ATOMIC create_activity_v2 RPC (same as the wizard + booking flow)
              // instead of sequential client INSERTs (activity row without
              // spots_total, booking, players — partial failures left orphans).
              // The RPC creates the court booking itself.
              //
              // UNIT TRAP: the grid form collects organizerFee in KRONOR
              // (legacy organizer_fee_per_person column) — v2 takes
              // organizer_fee_per_person_minor in ÖRE.
              const organizerFeeMinor = Math.round(((data.organizerFee as number) ?? 0) * 100);
              const activityBase = {
                match_type: data.matchType as string,
                sport_type: "padel",
                spots_total: maxPlayers,
                min_level: data.requireLevel ? (data.minLevel as number) : 1,
                max_level: data.requireLevel ? (data.maxLevel as number) : 10,
                visibility: "public",
                use_individual_pricing: true,
                organizer_fee_per_person_minor: organizerFeeMinor,
                apply_member_discount: true,
                payment_mode: "system",
              } as Record<string, unknown>;
              // hall_discount_* are NOT part of the v2 payload contract —
              // preserved via a follow-up UPDATE on the created row below.
              const hallDiscount = {
                hall_discount_type: (data.hallDiscountType as string) ?? "none",
                hall_discount_value: (data.hallDiscountValue as number) ?? 0,
              };

              // Helper to create a single activity (+ court booking via the RPC)
              // + pre-selected players for a given date.
              const createOneMatch = async (matchDate: Date): Promise<{ ok: boolean; conflict: boolean }> => {
                const dateStr = format(matchDate, "yyyy-MM-dd");
                const matchStart = new Date(matchDate);
                matchStart.setHours(selectionInfo.startTime.getHours(), selectionInfo.startTime.getMinutes(), 0, 0);
                const matchEnd = new Date(matchDate);
                matchEnd.setHours(selectionInfo.endTime.getHours(), selectionInfo.endTime.getMinutes(), 0, 0);

                // Check court availability (kept — clean Swedish conflict toast
                // instead of an RPC error).
                const { data: conflicts } = await supabase
                  .from("bookings")
                  .select("id")
                  .eq("court_id", courtId)
                  .lt("start_time", matchEnd.toISOString())
                  .gt("end_time", matchStart.toISOString())
                  .limit(1);
                if (conflicts && conflicts.length > 0) {
                  return { ok: false, conflict: true };
                }

                const { data: activityId, error: omError } = await (supabase.rpc as any)(
                  "create_activity_v2",
                  {
                    p_org: organization.id,
                    p_activity: {
                      ...activityBase,
                      date: dateStr,
                      start_time: startTimeStr,
                      end_time: endTimeStr,
                    },
                    p_court_ids: [courtId],
                    p_status: selectedPlayers.length >= maxPlayers ? "full" : "open",
                  },
                );

                if (omError || !activityId) {
                  toast.error(t("scheduleGrid.couldNotCreateMatch") + " " + (omError?.message ?? ""));
                  return { ok: false, conflict: false };
                }

                // Preserve the grid-only hall-discount fields (not in the v2
                // contract) on the created row.
                if (hallDiscount.hall_discount_type !== "none" || hallDiscount.hall_discount_value !== 0) {
                  await supabase.from("activities").update(hallDiscount as any).eq("id", activityId);
                }

                // Insert pre-selected players
                if (selectedPlayers.length > 0) {
                  await supabase.from("activity_players").insert(
                    selectedPlayers.map((userId) => ({
                      activity_id: activityId,
                      user_id: userId,
                      status: "confirmed",
                    }))
                  );
                }

                return { ok: true, conflict: false };
              };

              if (recurring?.enabled && recurring.days.length > 0) {
                // Recurring: create matches for each day x week
                const baseDate = selectionInfo.startTime;
                const weekStart = startOfWeek(baseDate, { weekStartsOn: 1 }); // Monday
                let created = 0;
                let skipped = 0;

                for (let w = 0; w < recurring.weeks; w++) {
                  for (const dayNum of recurring.days) {
                    // dayNum: 1=Mon, 7=Sun → offset from weekStart (Monday)
                    const matchDate = addDays(addWeeks(weekStart, w), dayNum - 1);
                    // Skip dates in the past
                    if (matchDate < new Date(new Date().setHours(0, 0, 0, 0))) {
                      skipped++;
                      continue;
                    }
                    const result = await createOneMatch(matchDate);
                    if (result.ok) created++;
                    else if (result.conflict) skipped++;
                    else break; // hard error
                  }
                }

                if (created > 0) {
                  const msg = skipped > 0
                    ? t("scheduleGrid.matchesCreatedWithSkipped", { created, skipped })
                    : t("scheduleGrid.matchesCreated", { count: created });
                  toast.success(msg);
                  invalidateBookings();
                  clearSelection();
                }
              } else {
                // Single match
                const result = await createOneMatch(selectionInfo.startTime);
                if (result.ok) {
                  toast.success(t("scheduleGrid.activityCreated"));
                  invalidateBookings();
                  clearSelection();
                }
              }
            } catch (err: unknown) {
              toast.error(t("scheduleGrid.errorOccurred") + " " + (err instanceof Error ? err.message : String(err)));
            }
            setIsBooking(false);
            setBookingDialog(false);
          } else if (data.type === "recurring_block") {
            if (!selectionInfo || !user || !organization) return;
            if (!data.blockName || !(data.blockName as string).trim()) {
              toast.error(t("scheduleGrid.nameRequiredForBlock"));
              return;
            }
            setIsBooking(true);
            try {
              const { error } = await supabase.from("recurring_blocks" as any).insert({
                organization_id: organization.id,
                name: data.blockName as string,
                court_ids: (data.blockCourtIds as string[]) ?? selectionInfo.courtIds,
                day_of_week: selectionInfo.startTime.getDay(),
                start_time: format(selectionInfo.startTime, "HH:mm:ss"),
                end_time: format(selectionInfo.endTime, "HH:mm:ss"),
                valid_from: (data.blockFrom as string) || format(selectionInfo.startTime, "yyyy-MM-dd"),
                valid_until: (data.blockTo as string) || null,
                interval_weeks: (data.intervalWeeks as number) ?? 1,
                price: (data.price as number) ?? 0,
                customer_id: (data.customerId as string) || null,
                color: (data.color as string) || null,
                comment: (data.blockComment as string) || null,
                block_category: (data.blockCategory as string) || "revenue",
                estimated_value: (data.estimatedValue as number) || null,
                is_confirmed_revenue: (data.isConfirmedRevenue as boolean) ?? false,
                status: "active",
                created_by: user.id,
              });
              if (error) throw error;
              toast.success(t("scheduleGrid.recurringBlockCreated", { name: data.blockName }));
              invalidateBookings();
              clearSelection();
            } catch (err: unknown) {
              toast.error(t("scheduleGrid.couldNotCreateBlock") + " " + (err instanceof Error ? err.message : (err as any)?.message ?? String(err)));
            }
            setIsBooking(false);
            setBookingDialog(false);
          } else if (data.type === "subscription") {
            if (!selectionInfo || !user || !organization) return;
            const days = (data.daysOfWeek as number[]) ?? [];
            const subValidFrom = (data.validFrom as string) ?? "";
            if (days.length === 0) {
              toast.error(t("scheduleGrid.selectAtLeastOneDay"));
              return;
            }
            if (!subValidFrom) {
              toast.error(t("scheduleGrid.startDateRequired"));
              return;
            }
            setIsBooking(true);
            try {
              // Look up user_id from customer if available
              let subUserId: string | null = null;
              const subCustomerId = (data.customerId as string) ?? null;
              if (subCustomerId) {
                const { data: customer } = await supabase
                  .from("customers")
                  .select("user_id")
                  .eq("id", subCustomerId)
                  .single();
                subUserId = customer?.user_id ?? null;
              }

              const courtIdsToBook = selectionInfo.courtIds;
              const rows = days.flatMap((day) =>
                courtIdsToBook.map((courtId) => ({
                  organization_id: organization.id,
                  court_id: courtId,
                  user_id: subUserId,
                  customer_id: subCustomerId,
                  day_of_week: day,
                  start_time: format(selectionInfo.startTime, "HH:mm:ss"),
                  end_time: format(selectionInfo.endTime, "HH:mm:ss"),
                  valid_from: subValidFrom,
                  valid_until: (data.validUntil as string) || format(addDays(new Date(subValidFrom), 365), "yyyy-MM-dd"),
                  interval_weeks: (data.intervalWeeks as number) ?? 1,
                  price: (data.price as number) ?? 0,
                  payment_method: (data.paymentMethod as string) ?? "invoice",
                  comment: (data.subscriptionComment as string) ?? null,
                  status: "active",
                  created_by: user.id,
                }))
              );

              const { error } = await supabase.from("recurring_bookings" as any).insert(rows);
              if (error) throw error;

              toast.success(t("scheduleGrid.subscriptionCreated", { count: rows.length }));
              invalidateBookings();
              clearSelection();
            } catch (err: unknown) {
              toast.error(t("scheduleGrid.couldNotCreateSubscription") + " " + (err instanceof Error ? err.message : String(err)));
            }
            setIsBooking(false);
            setBookingDialog(false);
          } else {
            toast.info(t("scheduleGrid.comingSoon", { type: data.type }));
            setBookingDialog(false);
          }
        }}
      />

      {/* PR-AK — Sticky bottom bulk-action bar (shown only in multi-select mode + ≥1 selected) */}
      {multiSelectMode && selectedBookingIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 p-3 shadow-lg">
          <div className="max-w-screen-md mx-auto flex items-center gap-2">
            <span className="text-sm font-medium">
              {t("scheduleGrid.bulkSelectedCount", { defaultValue: "Markerade: {{count}} bokningar", count: selectedBookingIds.size })}
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedBookingIds(new Set())}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkCancelOpen(true)}
              >
                <XCircle className="h-4 w-4 mr-1" />
                {t("scheduleGrid.bulkCancelButton", { defaultValue: "Avboka markerade ({{count}})", count: selectedBookingIds.size })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* PR-AK — Bulk cancel confirmation dialog */}
      <BulkCancelConfirmDialog
        open={bulkCancelOpen}
        onOpenChange={setBulkCancelOpen}
        bookingIds={Array.from(selectedBookingIds)}
        onCompleted={(results) => {
          setSelectedBookingIds(new Set());
          setMultiSelectMode(false);
          invalidateBookings();
          const successes = results.filter((r) => r.success).length;
          const failures = results.length - successes;
          if (failures === 0) {
            toast.success(t("scheduleGrid.bulkCancelSuccess", { defaultValue: "Avbokade {{count}} bokningar", count: successes }));
          } else {
            toast.warning(t("scheduleGrid.bulkCancelPartial", { defaultValue: "Avbokade {{ok}}/{{total}}. {{failed}} misslyckades.", ok: successes, total: results.length, failed: failures }));
          }
        }}
      />
    </div>
  );
}
